import { BOTONES } from '../../../../../../componentes/botones';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule, AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { Operador } from 'modelos/src';
import { ContentCardComponent } from '../../../../../../componentes/content-card/content-card.component';
import {
  ICheckSimResult,
  SimsService,
} from '../../../../../../auxiliares/servicios/http/sims.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { SelectorSimDisponibleComponent } from '../../../../administracion/sims/selector-sim-disponible/selector-sim-disponible.component';
import { ISim } from 'modelos';

@Component({
  selector: 'app-sim-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    BOTONES,
    MatProgressSpinnerModule,
    AsyncPipe,
    ContentCardComponent,
    SelectorSimDisponibleComponent,
  ],
  templateUrl: './sim-config.component.html',
  styleUrls: ['./sim-config.component.scss'],
})
export class SimConfigComponent implements OnInit {
  private simsService = inject(SimsService);
  private helper = inject(HelperService);

  @Input() formGroup!: FormGroup;
  @Input() title = 'SIM';
  @Input() idExcluir?: string;
  @Input() operadores: Operador[] = [
    'Claro',
    'Movistar',
    'Personal',
    'Tuenti',
    'Otro',
  ];
  // Desde el ABM de SIMs: en edición el chequeo encontraría el equipo que ya
  // tiene puesta esta misma SIM, y "Mover aquí" no tiene equipo destino.
  @Input() verificarDuplicados = true;
  @Input() permitirMover = true;
  /** Buscador de SIMs disponibles (no en el alta de la SIM misma). */
  @Input() mostrarSelector = true;
  @Output() simMovida = new EventEmitter<void>();

  public filteredApnOptions!: Observable<string[]>;
  public duplicado?: ICheckSimResult['entidad'];
  public cargandoCheck = false;

  // Configuración completa de cada APN (incluyendo usuario y password)
  private apnConfigMap: Record<string, { usuario: string; password: string }> =
    {
      // Personal
      'gprs.personal.com': { usuario: 'gprs', password: 'adgj' },
      'm2m.personal.com': { usuario: '', password: '' },
      'datos.personal.com': { usuario: 'datos', password: 'datos' },
      'internet.personal.com': { usuario: 'gprs', password: 'adgb' },
      // Claro
      'internet.ctimovil.com.ar': { usuario: '', password: '' },
      'igprs.claro.com.ar': { usuario: 'wap', password: 'wap' },
      // Movistar
      'internet.gprs.unifon.com.ar': { usuario: '', password: '' },
      'internet.movistar.com.co': { usuario: 'movistar', password: 'movistar' },
      'm2m.movistar': { usuario: 'movistar', password: 'movistar' },
      'gm2m.movistar': { usuario: 'gm2m', password: 'gm2m' },
      'wap.gprs.unifon.com.ar': { usuario: 'wap', password: 'wap' },
      // Tuenti
      'tuenti.com.ar': { usuario: '', password: '' },
      'web.tuenti.com.ar': { usuario: '', password: '' },
    };

  // Opciones de APN disponibles por operador (primera opción es la predeterminada)
  private apnOptions: Record<Operador, string[]> = {
    Claro: ['igprs.claro.com.ar', 'internet.ctimovil.com.ar'],
    Movistar: [
      'internet.gprs.unifon.com.ar',
      'wap.gprs.unifon.com.ar',
      'm2m.movistar',
      'gm2m.movistar',
      'internet.movistar.com.co',
    ],
    Personal: [
      'datos.personal.com',
      'internet.personal.com',
      'gprs.personal.com',
      'm2m.personal.com',
    ],
    Tuenti: ['tuenti.com.ar', 'web.tuenti.com.ar'],
    Otro: [],
  };

  ngOnInit(): void {
    this.setupApnAutocomplete();
  }

  /**
   * Configura el autocomplete de APN basado en el operador seleccionado
   */
  private setupApnAutocomplete(): void {
    const apnControl = this.formGroup.get('apn');
    const operadorControl = this.formGroup.get('operador');

    if (!apnControl || !operadorControl) return;

    this.filteredApnOptions = apnControl.valueChanges.pipe(
      startWith(apnControl.value || ''),
      map((value) => {
        const operador = operadorControl.value as Operador;
        return this._filterApn(value || '', operador);
      }),
    );
  }

  /**
   * Filtra las opciones de APN según el texto ingresado y el operador seleccionado
   */
  private _filterApn(value: string, operador: Operador): string[] {
    if (!operador || !this.apnOptions[operador]) return [];

    const options = this.apnOptions[operador];

    // Si el valor está vacío o coincide exactamente con una opción, mostrar todas
    if (!value || options.some((opt) => opt === value)) {
      return options;
    }

    // Filtrar por el texto ingresado
    const filterValue = value.toLowerCase();
    return options.filter((option) =>
      option.toLowerCase().includes(filterValue),
    );
  }

  /**
   * Se ejecuta cuando el usuario selecciona una APN del autocomplete
   * Autocompleta usuario y password según la APN seleccionada
   */
  public onApnSelected(apn: string): void {
    if (!apn || !this.apnConfigMap[apn]) return;

    const config = this.apnConfigMap[apn];

    // Autocompletar usuario y password según la APN
    this.formGroup.patchValue({
      usuario: config.usuario,
      password: config.password,
    });
  }

  /**
   * Se ejecuta cuando el usuario hace focus en el campo APN
   * Muestra todas las opciones disponibles del operador actual
   */
  public onApnFocus(): void {
    const apnControl = this.formGroup.get('apn');
    if (apnControl) {
      // Emitir el valor actual para refrescar las opciones
      apnControl.updateValueAndValidity({ emitEvent: true });
    }
  }

  /**
   * Se ejecuta cuando el usuario selecciona un operador
   * Autocompleta los campos APN, usuario y password según el operador
   */
  public onOperadorChange(operador: Operador): void {
    if (!operador || !this.apnOptions[operador]) return;

    const opciones = this.apnOptions[operador];

    // Si hay opciones, usar la primera como predeterminada
    if (opciones.length > 0) {
      const apnPredeterminada = opciones[0];
      const config = this.apnConfigMap[apnPredeterminada];

      this.formGroup.patchValue({
        apn: apnPredeterminada,
        usuario: config?.usuario || '',
        password: config?.password || '',
      });
    } else {
      // Operador "Otro" - limpiar campos
      this.formGroup.patchValue({
        apn: '',
        usuario: '',
        password: '',
      });
    }

    // Refrescar las opciones de autocomplete
    this.setupApnAutocomplete();
  }

  /**
   * Verifica si el ICCID o número ya está asignado a otro dispositivo
   */
  public async checkDuplicado(): Promise<void> {
    if (!this.verificarDuplicados) return;
    const iccid = this.formGroup.get('iccid')?.value || undefined;
    const numero = this.formGroup.get('numero')?.value || undefined;

    if (!iccid && !numero) {
      this.duplicado = undefined;
      return;
    }

    this.cargandoCheck = true;
    try {
      const result = await this.simsService.check(
        iccid,
        numero,
        this.idExcluir,
      );
      this.duplicado = result.entidad;
    } catch {
      // Ignorar errores de chequeo
    } finally {
      this.cargandoCheck = false;
    }
  }

  /** Hay una SIM vinculada o elegida en el selector. */
  public get conSim(): boolean {
    return !!this.formGroup.get('_id')?.value;
  }

  /** SIM elegida en el selector (o `null` = cargar una nueva). */
  public alSeleccionar(sim: ISim | null): void {
    SimsService.aplicarSeleccion(this.formGroup, sim);
    this.duplicado = undefined;
  }

  public get tipoLabel(): string {
    if (!this.duplicado) return '';
    return this.duplicado.tipo === 'tracker'
      ? 'tracker'
      : 'dispositivo de alarma';
  }

  public async moverSim(): Promise<void> {
    if (!this.duplicado) return;
    try {
      await this.simsService.liberar({
        idEntidad: this.duplicado._id,
        tipo: this.duplicado.tipo,
        simNumero: this.duplicado.simNumero,
      });
      this.duplicado = undefined;
      this.helper.notifSuccess('SIM liberada del dispositivo anterior');
      this.simMovida.emit();
    } catch (error) {
      this.helper.notifError(error);
    }
  }
}
